import { isEqual } from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import XLSX from 'xlsx';
import { Button } from '@praktio/common-ui';
import { Col, Container, Row } from 'react-bootstrap';
import { Helmet } from 'react-helmet';

import { RootState } from '../../ducks/store';
import { getTeams } from '../../ducks/teams';
import { bulkUploadUsers } from '../../ducks/users';
import { buildBulkTableData } from '../../utils/functions';
import { TeamSize, CLESize, BulkUploadHeaders, BulkEditHeaders } from '../../utils/constants';
import ActionBar from '../common/ActionBar';
import { Table } from '../common/Table/Table';

const BulkUploadUsers = () => {
  const dispatch = useDispatch();
  const { teams } = useSelector((state: RootState) => {
    const teams = state.teams.list;
    return { teams };
  });

  useEffect(() => {
    dispatch(getTeams());
  }, [dispatch]);

  const [tableData, setTableData] = useState<any[]>([]);
  const [allValid, setAllValid] = useState(false);
  const prevTableData = useRef<any[]>([]);

  const onSaveClick = async () => {
    const users = tableData.map((d) => {
      const {
        email,
        firstName,
        lastName,
        password,
        practiceGroup,
        firmOffice,
        currentPosition,
        noCle,
      } = d;

      const user: IUser = {
        email,
        firstName,
        lastName,
        password,
        practiceGroup,
        firmOffice,
        position: currentPosition,
        teams: [],
        cles: [],
      };

      for (let i = 1; i <= TeamSize; i++) {
        const teamId = d[`team${i}id`];
        if (teamId) {
          user.teams?.push({ _id: teamId } as any);
        }
      }

      for (let i = 1; i <= CLESize; i++) {
        const state = d[`cleState${i}`];
        const barNumber = d[`cleBarNumber${i}`];
        if (state || barNumber) {
          user.cles?.push({ state, barNumber });
        }
      }

      if (noCle) {
        user.optOutCLEs = true;
      }

      return user;
    });

    if (users.length > 0) {
      const existingUsers = (await dispatch(bulkUploadUsers(users))) as unknown as any[];
      const unprocessedUsers: any[] = [];
      for (const user of existingUsers || []) {
        const foundUser = tableData.find(
          (u) => u.email?.trim()?.toLowerCase() === user.email.trim().toLowerCase(),
        );

        if (foundUser) {
          unprocessedUsers.push({ ...foundUser, _id: user._id, hasError: user.hasError });
        }
      }

      setTableData(unprocessedUsers);
    }
  };

  const hasChanges = tableData?.length && !isEqual(tableData, prevTableData.current);
  const hasExisting = !!tableData?.find((d) => !!d._id);
  return (
    <Container fluid className="BulkUploadUsers">
      <Helmet>
        <title>Bulk Import Users - Praktio</title>
      </Helmet>
      <ActionBar flex>
        <Button disabled={!allValid || !hasChanges} btnStyle="primary" onClick={onSaveClick}>
          SAVE
        </Button>
        <Button
          className="secondary"
          onClick={() => {
            const ws = XLSX.utils.aoa_to_sheet([BulkUploadHeaders.map((h) => h.Header)]);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'BulkUpload');
            XLSX.writeFile(wb, 'BulkUploadTemplate.xlsx');
          }}
        >
          DOWNLOAD TEMPLATE
        </Button>
        <Button
          className="secondary"
          type="file"
          accept=".xlsx"
          onFileSelect={(e) => {
            if (e?.target.files && e.target.files.length > 0) {
              const file = e?.target.files[0];
              if (file) {
                const fileReader = new FileReader();
                fileReader.onload = (e) => {
                  if (e?.target?.result) {
                    const results = buildBulkTableData(
                      BulkUploadHeaders,
                      e.target.result as ArrayBuffer,
                      teams,
                    );

                    setTableData(results.data.filter((d) => !!d.email));
                    setAllValid(results.valid);
                  }
                };

                fileReader.readAsArrayBuffer(file as unknown as Blob);
              }
            }
          }}
        >
          IMPORT USERS
        </Button>
        <Button
          className="secondary"
          onClick={() => {
            const sheetData: any[][] = [BulkEditHeaders.map((h) => h.Header)];
            tableData?.forEach((d) => {
              const sheetRow: string[] = [];
              BulkEditHeaders.forEach((c) => {
                let accessor = c.accessor;
                if (accessor.startsWith('team')) {
                  accessor += 'id';
                }

                let value = d[accessor];
                sheetRow.push(value);
              });
              sheetData.push(sheetRow);
            });

            const ws = XLSX.utils.aoa_to_sheet(sheetData);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'ExportedBulkUpload');
            XLSX.writeFile(wb, 'ExportedBulkUpload.xlsx');
          }}
        >
          EXPORT TABLE FOR EDITS
        </Button>
      </ActionBar>
      {hasExisting && (
        <div key={`warning`} className="alert alert-danger" role="alert">
          <div className="alert-title">Existing Users</div>
          <div className="alert-body">
            Rows highlighted in red are users that already exist and cannot be imported. Click
            'EXPORT TABLE FOR EDITS' to download a sheet of that data for Bulk Edits.
          </div>
        </div>
      )}
      <Row>
        <Col xs={3} md={3}>
          <Table
            tableTitle="Teams"
            columns={[
              { Header: 'Team', accessor: 'name' },
              { Header: 'ID', accessor: '_id', isId: true },
            ]}
            data={teams || []}
          />
        </Col>
        <Col xs={9} md={9}>
          <Table tableTitle="New Users" columns={BulkUploadHeaders} data={tableData} noPaging />
          <div className="mt-2">
            <span className="required-notice">
              CLE Jurisdiction must be fully spelled out with title capitalization (e.g. New York)
            </span>
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default BulkUploadUsers;
